import React, { useRef, createElement } from 'react';
import { VARIANT_COLORS } from './DjayAvatar';
// Import note (audit-referentie: Ronde 10): `createElement` is NOT needed —
// JSX is transformed by the bundler. Regressed in ronde 6 and ronde 8.
export type DjayToetsDomain = 'rekenen' | 'taalverzorging' | 'begrijpend-lezen' | 'toets';
export interface DjayToetsAvatarProps {
  domain?: DjayToetsDomain;
  size?: 'sm' | 'md' | 'lg';
  animate?: boolean;
  className?: string;
}
const SIZES = {
  sm: 48,
  md: 80,
  lg: 120
};
// Reuse the canonical default-variant palette so this avatar never drifts
// out of sync with DjayAvatar.
const colors = VARIANT_COLORS.default;
let _stylesInjected = false;
function ensureAnimationStyles() {
  if (_stylesInjected || typeof document === 'undefined') return;
  _stylesInjected = true;
  const style = document.createElement('style');
  style.setAttribute('data-djay-toets-avatar', 'animations');
  style.textContent = `
    .djay-toets-animated .djay-eyes { transform-origin: center; animation: djay-blink 4s ease-in-out infinite; }
    @keyframes djay-blink { 0%, 90%, 100% { transform: scaleY(1); } 95% { transform: scaleY(0.1); } }
    .djay-toets-animated .djay-body { transform-origin: center bottom; animation: djay-wobble 6s ease-in-out infinite; }
    @keyframes djay-wobble { 0%, 100% { transform: rotate(0deg); } 25% { transform: rotate(-1.5deg); } 75% { transform: rotate(1.5deg); } }
    .djay-toets-animated .djay-flame { transform-origin: center bottom; animation: djay-flame-flicker 3s ease-in-out infinite; }
    @keyframes djay-flame-flicker { 0%, 100% { transform: scaleY(1) scaleX(1); } 33% { transform: scaleY(1.08) scaleX(0.95); } 66% { transform: scaleY(0.95) scaleX(1.05); } }
  `;
  document.head.appendChild(style);
}
export function DjayToetsAvatar({
  domain = 'toets',
  size = 'md',
  animate = false,
  className = ''
}: DjayToetsAvatarProps) {
  if (animate) {
    ensureAnimationStyles();
  }
  const uid = useRef(Math.random().toString(36).slice(2, 9)).current;
  const pixelSize = SIZES[size];
  const renderHeldItem = () => {
    switch (domain) {
      case 'rekenen':
        return <g transform="translate(155, 90) rotate(15)" data-id="element-3709" data-mp="3662i7udnjrmr">
            <rect x="0" y="0" width="24" height="36" rx="3" fill="#CBD5E1" stroke="#94A3B8" strokeWidth="2" data-id="element-3710" data-mp="318131kjckcgk" />
            
            <rect x="4" y="4" width="16" height="8" rx="1" fill="#86EFAC" data-id="element-3711" data-mp="2wasi5c6tdubm" />
            <rect x="4" y="16" width="4" height="4" rx="1" fill="#475569" data-id="element-3712" data-mp="3txrfzq98t8b3" />
            <rect x="10" y="16" width="4" height="4" rx="1" fill="#475569" data-id="element-3713" data-mp="3oy54pmcz624x" />
            <rect x="16" y="16" width="4" height="4" rx="1" fill="#475569" data-id="element-3714" data-mp="3ow25cbpxmwnj" />
            <rect x="4" y="22" width="4" height="4" rx="1" fill="#475569" data-id="element-3715" data-mp="3jxh3y8yy26xi" />
            <rect x="10" y="22" width="4" height="4" rx="1" fill="#475569" data-id="element-3716" data-mp="3f0qsjyy33zif" />
            <rect x="16" y="22" width="4" height="4" rx="1" fill="#475569" data-id="element-3717" data-mp="28jn5t7031k62" />
            <rect x="4" y="28" width="10" height="4" rx="1" fill="#475569" data-id="element-3718" data-mp="28hiqzu0nb0r0" />
            <rect x="16" y="28" width="4" height="4" rx="1" fill="#F59E0B" data-id="element-3719" data-mp="23jh6d4nkmtdf" />
          </g>;
      case 'taalverzorging':
        return <g transform="translate(165, 85) rotate(35)" data-id="element-3720" data-mp="j0x0bxcbvca9">
            <path d="M0 0 L6 0 L4 30 L2 30 Z" fill="#1E3A8A" data-id="element-3721" data-mp="o2mbdczjvpyc" />
            <path d="M0 0 L3 -8 L6 0 Z" fill="#FBBF24" data-id="element-3722" data-mp="t0ff7zdds7vg" />
            <path d="M3 -8 L3 0" stroke="#B45309" strokeWidth="0.5" data-id="element-3723" data-mp="szmdxdx6yakp" />
          </g>;
      case 'begrijpend-lezen':
        return <g transform="translate(145, 100) rotate(-10)" data-id="element-3724" data-mp="xtguw9lewbg3">
            <path d="M0 0 Q10 -5 20 0 L20 20 Q10 15 0 20 Z" fill="#FEF3C7" stroke="#D97706" strokeWidth="1" data-id="element-3725" data-mp="12pz1lf0ayjv2" />
            
            <path d="M20 0 Q30 -5 40 0 L40 20 Q30 15 20 20 Z" fill="#FEF3C7" stroke="#D97706" strokeWidth="1" data-id="element-3726" data-mp="16uf4d7emdb62" />
            
            <path d="M20 0 L20 20" stroke="#D97706" strokeWidth="2" data-id="element-3727" data-mp="1gqi7afhapdu2" />
            <line x1="4" y1="4" x2="16" y2="4" stroke="#D97706" strokeWidth="1" strokeOpacity="0.5" data-id="element-3728" data-mp="1gofgvuc8diot" />
            
            <line x1="4" y1="8" x2="16" y2="8" stroke="#D97706" strokeWidth="1" strokeOpacity="0.5" data-id="element-3729" data-mp="3dl4tsajzke1j" />
            
            <line x1="4" y1="12" x2="12" y2="12" stroke="#D97706" strokeWidth="1" strokeOpacity="0.5" data-id="element-3730" data-mp="1ctcddquqiluo" />
            
            <line x1="24" y1="4" x2="36" y2="4" stroke="#D97706" strokeWidth="1" strokeOpacity="0.5" data-id="element-3731" data-mp="17vavvacyiulb" />
            
            <line x1="24" y1="8" x2="36" y2="8" stroke="#D97706" strokeWidth="1" strokeOpacity="0.5" data-id="element-3732" data-mp="1lt992cfjkkz8" />
            
            <line x1="24" y1="12" x2="32" y2="12" stroke="#D97706" strokeWidth="1" strokeOpacity="0.5" data-id="element-3733" data-mp="1ht73hs6or7eo" />
            
          </g>;
      case 'toets':
      default:
        return <g transform="translate(155, 90) rotate(10)" data-id="element-3734" data-mp="p4t7tj32kn6a">
            <rect x="0" y="0" width="26" height="34" rx="2" fill="#D4A373" data-id="element-3735" data-mp="p2709jg6i7o2" />
            <rect x="2" y="4" width="22" height="28" fill="#FFFFFF" data-id="element-3736" data-mp="12wpu36i242ur" />
            <rect x="8" y="-2" width="10" height="6" rx="1" fill="#9CA3AF" data-id="element-3737" data-mp="y3n93mrci1q6" />
            <circle cx="13" cy="1" r="1" fill="#4B5563" data-id="element-3738" data-mp="aelwdnbhc4f3" />
            <path d="M6 10 L10 14 L18 6" fill="none" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-3739" data-mp="ad11txp60lh8" />
            
            <path d="M6 20 L10 24 L18 16" fill="none" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-3740" data-mp="3seg75p4uu09w" />
            
          </g>;
    }
  };
  return <div className={`inline-flex items-center justify-center ${animate ? 'djay-toets-animated' : ''} ${className}`} style={{
    width: pixelSize,
    height: pixelSize
  }} data-id="element-3741" data-mp="3igtmem0vjei0">
      
      <svg viewBox="0 0 200 220" width="100%" height="100%" fill="none" xmlns="http://www.w3.org/2000/svg"
    // Decoratief: Djay staat naast een toetsscherm dat zijn eigen kop en
    // uitleg heeft. Zonder deze markering leest een schermlezer een groep
    // van ~180 naamloze vormen voor — ruis vóór de eigenlijke inhoud, en
    // precies op een scherm waar een kind zich moet concentreren.
    aria-hidden="true" focusable="false" style={{
      overflow: 'visible'
    }} data-id="element-3742" data-mp="3ndsgj4kjflg7">
        
        <defs data-id="element-3743" data-mp="2pqtf6giwhpbz">
          <linearGradient id={`${uid}-body-grad`} x1="100" y1="45" x2="100" y2="195" gradientUnits="userSpaceOnUse" data-id="element-3744" data-mp="2puh9vj030hsa">
            
            <stop offset="0%" stopColor={colors.bodyGradient[0]} data-id="element-3745" data-mp="11909jnpxrg5c" />
            <stop offset="50%" stopColor={colors.bodyGradient[1]} data-id="element-3746" data-mp="11abg1varn205" />
            <stop offset="100%" stopColor={colors.bodyGradient[2]} data-id="element-3747" data-mp="w9xi562vp8fu" />
          </linearGradient>
        </defs>

        {/* Left arm resting */}
        <path d="M48 150 C38 142, 24 140, 18 152 C12 164, 22 178, 32 176 C42 174, 34 160, 24 158 C18 156, 20 168, 26 172" stroke={colors.armColor} strokeWidth="7" strokeLinecap="round" fill="none" data-id="element-3748" data-mp="mjm06fmd0ypy" />
        
        <path d="M46 148 C38 141, 26 140, 20 150" stroke={colors.armHighlight} strokeWidth="3" strokeLinecap="round" fill="none" data-id="element-3749" data-mp="dj5xv9hcrxmo" />
        
        <circle cx="26" cy="171" r="4" fill={colors.armColor} data-id="element-3750" data-mp="8kmd3cehwrvw" />
        <circle cx="26" cy="171" r="2.5" fill={colors.armHighlight} data-id="element-3751" data-mp="3l9r0tvg7oud" />

        {/* Right arm extended (behind item) */}
        <path d="M152 140 Q165 125 170 115" stroke={colors.armColor} strokeWidth="8" strokeLinecap="round" fill="none" data-id="element-3752" data-mp="22wsz768xgt2z" />
        
        <path d="M152 140 Q165 125 170 115" stroke={colors.armHighlight} strokeWidth="3" strokeLinecap="round" fill="none" data-id="element-3753" data-mp="1xzkeoz0b4cyk" />
        

        <g className="djay-body" data-id="element-3754" data-mp="2ahd9mrjhvlfg">
          <path d="M100 195 C55 195, 35 155, 35 115 C35 65, 60 45, 100 45 C140 45, 165 65, 165 115 C165 155, 145 195, 100 195 Z" fill={`url(#${uid}-body-grad)`} data-id="element-3755" data-mp="2agbpzyoexqm4" />
          
        </g>

        <g className="djay-flame" data-id="element-3756" data-mp="2k7g2mrqm8zds">
          <path d="M100 52 C88 46, 83 34, 86 18 C90 8, 100 2, 100 2 C100 2, 110 8, 114 18 C117 34, 112 46, 100 52 Z" fill={colors.flameColors[0]} data-id="element-3757" data-mp="2k6n1q7ec943k" />
          
          <path d="M100 50 C89 45, 85 34, 88 20 C91 10, 100 5, 100 5 C100 5, 109 10, 112 20 C115 34, 111 45, 100 50 Z" fill={colors.flameColors[1]} data-id="element-3758" data-mp="1rhzh8e1eyir2" />
          
          <path d="M100 48 C93 44, 90 35, 92 24 C95 15, 100 11, 100 11 C100 11, 105 15, 108 24 C110 35, 107 44, 100 48 Z" fill={colors.flameColors[2]} data-id="element-3759" data-mp="1wjyj1jhi97lw" />
          
          <path d="M97 40 C94 36, 93 29, 95 23 C96 19, 98 17, 98 17 C98 17, 96 24, 97 31 C97 35, 97 38, 97 40 Z" fill={colors.flameColors[3]} data-id="element-3760" data-mp="37vzchbpbdk7z" />
          
        </g>

        <g className="djay-glasses" data-id="element-3761" data-mp="1tz1i925u0cd">
          <path d="M90 92 C96 87, 104 87, 110 92" stroke={colors.glassesFrame} strokeWidth="4" strokeLinecap="round" fill="none" data-id="element-3762" data-mp="1v1oi5wd3in0" />
          
          <rect x="49" y="82" width="42" height="36" rx="7" ry="7" fill="none" stroke={colors.glassesFrame} strokeWidth="5" data-id="element-3763" data-mp="bkbijrarnbdr" />
          
          <rect x="52" y="85" width="36" height="30" rx="5" ry="5" fill={colors.glassesLens} data-id="element-3764" data-mp="6sjzs2xuq9y6" />
          
          <rect x="55" y="87" width="12" height="6" rx="3" ry="3" fill="white" fillOpacity="0.55" data-id="element-3765" data-mp="kjotkoejg80l" />
          
          <rect x="109" y="82" width="42" height="36" rx="7" ry="7" fill="none" stroke={colors.glassesFrame} strokeWidth="5" data-id="element-3766" data-mp="giv4vst9up6n" />
          
          <rect x="112" y="85" width="36" height="30" rx="5" ry="5" fill={colors.glassesLens} data-id="element-3767" data-mp="pk32e8nyrlzo" />
          
          <rect x="115" y="87" width="12" height="6" rx="3" ry="3" fill="white" fillOpacity="0.55" data-id="element-3768" data-mp="2waamzqslr2bm" />
          
          <path d="M49 96 L40 94" stroke={colors.glassesFrame} strokeWidth="4" strokeLinecap="round" data-id="element-3769" data-mp="2rbzcreyeapsv" />
          
          <path d="M151 96 L160 94" stroke={colors.glassesFrame} strokeWidth="4" strokeLinecap="round" data-id="element-3770" data-mp="16t5eseijqrhd" />
          
        </g>

        {/* Eyes: Focused/Attentive */}
        <g className="djay-eyes" data-id="element-3771" data-mp="x3lrmwnswbgp">
          <path d="M62 98 Q70 92 78 98" stroke={colors.mouthColor} strokeWidth="3" strokeLinecap="round" fill="none" data-id="element-3772" data-mp="x2khcuelzt01" />
          
          <path d="M122 98 Q130 92 138 98" stroke={colors.mouthColor} strokeWidth="3" strokeLinecap="round" fill="none" data-id="element-3773" data-mp="120vrl68tg5is" />
          
          <circle cx="70" cy="102" r="4" fill={colors.mouthColor} data-id="element-3774" data-mp="t20gpgutu795" />
          <circle cx="130" cy="102" r="4" fill={colors.mouthColor} data-id="element-3775" data-mp="t3bodrre7z5j" />
        </g>

        {/* Mouth: Slight smile */}
        <path d="M92 125 Q100 132 108 125" stroke={colors.mouthColor} strokeWidth="3" strokeLinecap="round" fill="none" data-id="element-3776" data-mp="e745curq13ru" />
        

        <g className="djay-feet" data-id="element-3777" data-mp="28i90faoqs7vk">
          <ellipse cx="80" cy="196" rx="14" ry="6" fill={colors.feetDark} data-id="element-3778" data-mp="28jadtlt8d6gg" />
          <ellipse cx="80" cy="195" rx="14" ry="6" fill={colors.feetLight} data-id="element-3779" data-mp="2dgsqnnadc5tq" />
          <ellipse cx="120" cy="196" rx="14" ry="6" fill={colors.feetDark} data-id="element-3780" data-mp="2jg1nsvgqajbw" />
          <ellipse cx="120" cy="195" rx="14" ry="6" fill={colors.feetLight} data-id="element-3781" data-mp="2jdxc3rcl8g12" />
        </g>

        {/* Held Item */}
        {renderHeldItem()}

        {/* Right hand (thumb/fingers) over the item */}
        <circle cx="168" cy="112" r="4" fill={colors.armColor} data-id="element-3782" data-mp="29frf9o5n2032" />
        <circle cx="168" cy="112" r="2.5" fill={colors.armHighlight} data-id="element-3783" data-mp="2eh6wf54p3mg6" />
      </svg>
    </div>;
}